import React, { useState } from 'react';
import { Icons } from './Icons';
import { useTree } from '../contexts/TreeContext';
import { byName } from '../utils/helpers';

const selectStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1.5px solid #D5CAB8', background: '#fff',
  fontSize: 13, color: '#2C2418', fontFamily: "'DM Sans', sans-serif",
  outline: 'none', boxSizing: 'border-box',
};

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1.5px solid #D5CAB8', background: '#fff',
  fontSize: 13, color: '#2C2418', outline: 'none',
  fontFamily: "'DM Sans', sans-serif", boxSizing: 'border-box',
};

const labelStyle = {
  fontSize: 12, fontWeight: 600, color: '#6B5E4F',
  display: 'block', marginBottom: 6,
  textTransform: 'uppercase', letterSpacing: 0.8,
};

export default function AddPersonModal({ onClose, existingPeople }) {
  const { addPerson, batchUpdatePeople } = useTree();
  const [saving, setSaving] = useState(false);

  // Person fields
  const [firstName, setFirstName]       = useState('');
  const [lastName, setLastName]         = useState('');
  const [birth, setBirth]               = useState('');
  const [death, setDeath]               = useState('');
  const [birthPlace, setBirthPlace]     = useState('');
  const [deathPlace, setDeathPlace]     = useState('');
  const [occupation, setOccupation]     = useState('');
  const [gender, setGender]             = useState('M');
  const [parentIds, setParentIds]       = useState([]);

  // Marriage(s)
  const [marriages, setMarriages] = useState([]);

  const addMarriageSlot = () => {
    setMarriages([...marriages, {
      spouseId: '', marriageDate: '', marriagePlace: '',
      status: 'current', endReason: '', endDate: '',
    }]);
  };

  const updateMarriage = (idx, field, value) => {
    const copy = [...marriages];
    copy[idx] = { ...copy[idx], [field]: value };
    if (field === 'status' && value === 'current') {
      copy[idx].endReason = '';
      copy[idx].endDate = '';
    }
    setMarriages(copy);
  };

  const removeMarriage = (idx) => {
    setMarriages(marriages.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!firstName.trim()) return;
    setSaving(true);

    const newParentIds = parentIds.filter(Boolean);
    const newMarriages = marriages
      .filter(m => m.spouseId || m.marriageDate)
      .map(m => ({
        spouseId: m.spouseId || null,
        marriageDate: m.marriageDate || null,
        marriagePlace: m.marriagePlace || null,
        status: m.status,
        endReason: m.status === 'ended' ? (m.endReason || null) : null,
        endDate: m.status === 'ended' ? (m.endDate || null) : null,
        children: [],
      }));

    // The create carries the person's own attributes only. Every relationship —
    // both halves of it — goes in the single batch below, because a marriage
    // written at create time and mirrored onto the spouse afterwards is two
    // writes, and an interruption between them left the marriage recorded on
    // one document and not the other. Now an interruption leaves a person with
    // no relationships at all: visible, symmetric, and fixable in the UI.
    // A person with no relationships is also what the server's contributor
    // rules expect a follow-up wiring call to be aimed at (is_unlinked in
    // api/authz.py), so this is the sanctioned shape rather than a workaround.
    const personData = {
      name: `${firstName.trim()} ${lastName.trim()}`.trim(),
      birth: birth || null,
      death: death || null,
      birthPlace: birthPlace || null,
      deathPlace: deathPlace || null,
      occupation: occupation || null,
      gender,
      parentIds: [],
      marriages: [],
      children: [],
      photoURL: null,
    };

    const newPersonId = await addPerson(personData);

    // Keyed by personId so each document is written once, matching
    // EditRelationshipsModal — a Firestore batch that touches the same document
    // twice is not something to rely on.
    const updatesMap = new Map();
    const merge = (id, data) => updatesMap.set(id, { ...(updatesMap.get(id) || {}), ...data });

    if (newParentIds.length || newMarriages.length) {
      merge(newPersonId, {
        ...(newParentIds.length ? { parentIds: newParentIds } : {}),
        ...(newMarriages.length ? { marriages: newMarriages } : {}),
      });
    }

    // Mirror each marriage onto the spouse's record
    for (const m of newMarriages) {
      if (!m.spouseId || !existingPeople[m.spouseId]) continue;
      const spouseMarriages = [...(existingPeople[m.spouseId].marriages || [])];
      // Avoid duplicates (shouldn't happen on add, but be safe)
      if (spouseMarriages.find(sm => sm.spouseId === newPersonId)) continue;
      spouseMarriages.push({
        spouseId: newPersonId,
        marriageDate: m.marriageDate || null,
        marriagePlace: m.marriagePlace || null,
        status: m.status,
        endReason: m.status === 'ended' ? (m.endReason || null) : null,
        endDate: m.status === 'ended' ? (m.endDate || null) : null,
        children: [],
      });
      merge(m.spouseId, { marriages: spouseMarriages });
    }

    // Mirror each parent → add new person to their children array
    for (const pid of newParentIds) {
      if (!existingPeople[pid]) continue;
      merge(pid, { children: [...new Set([...(existingPeople[pid].children || []), newPersonId])] });
    }

    if (updatesMap.size) {
      await batchUpdatePeople([...updatesMap].map(([personId, data]) => ({ personId, data })));
    }

    setSaving(false);
    onClose();
  };

  const peopleList = Object.values(existingPeople || {}).sort(byName);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(44,36,24,0.5)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        width: 560, maxHeight: '88vh', background: '#FDFBF7', borderRadius: 16,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden',
        fontFamily: "'DM Sans', sans-serif",
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #E8E0D4',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: '#F5EDE3',
        }}>
          <h3 style={{ margin: 0, fontSize: 18, fontFamily: "'Crimson Pro', Georgia, serif", color: '#2C2418', fontWeight: 700 }}>Add New Person</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8B7D6B' }}>
            <Icons.Close />
          </button>
        </div>

        {/* Form body */}
        <div style={{ padding: 24, overflowY: 'auto', maxHeight: 'calc(88vh - 140px)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="First Name" value={firstName} onChange={setFirstName} placeholder="e.g. John" />
            <Field label="Last Name" value={lastName} onChange={setLastName} placeholder="e.g. Mitchell" />
            <Field label="Date of Birth" value={birth} onChange={setBirth} placeholder="YYYY-MM-DD" />
            <Field label="Date of Death" value={death} onChange={setDeath} placeholder="Leave empty if living" />
            <Field label="Birth Place" value={birthPlace} onChange={setBirthPlace} placeholder="City, State, Country" />
            <Field label="Death Place" value={deathPlace} onChange={setDeathPlace} placeholder="City, State, Country" />
            <Field label="Occupation" value={occupation} onChange={setOccupation} placeholder="e.g. Teacher" span={2} />

            {/* Gender */}
            <div style={{ gridColumn: 'span 2' }}>
              <label style={labelStyle}>Gender</label>
              <div style={{ display: 'flex', gap: 10 }}>
                {[['M', 'Male'], ['F', 'Female'], ['O', 'Other']].map(([v, l]) => (
                  <button key={v} onClick={() => setGender(v)} style={{
                    flex: 1, padding: '9px 0', borderRadius: 8,
                    border: gender === v ? '2px solid #C4975A' : '1.5px solid #D5CAB8',
                    background: gender === v ? '#F5EDE3' : '#fff',
                    color: gender === v ? '#C4975A' : '#6B5E4F',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    fontFamily: "'DM Sans', sans-serif",
                  }}>{l}</button>
                ))}
              </div>
            </div>

            {/* Parents */}
            {peopleList.length > 0 && (
              <div style={{ gridColumn: 'span 2' }}>
                <label style={labelStyle}>Parents (select from existing people)</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  <select value={parentIds[0] || ''} onChange={e => { const v = [...parentIds]; v[0] = e.target.value; setParentIds(v); }} style={selectStyle}>
                    <option value="">— Parent 1 —</option>
                    {peopleList.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                  <select value={parentIds[1] || ''} onChange={e => { const v = [...parentIds]; v[1] = e.target.value; setParentIds(v); }} style={selectStyle}>
                    <option value="">— Parent 2 —</option>
                    {peopleList.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* ── Marriages ── */}
          <div style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h4 style={{ margin: 0, fontSize: 12, color: '#8B7D6B', textTransform: 'uppercase', letterSpacing: 1.2, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icons.Ring /> Marriages
              </h4>
              <button onClick={addMarriageSlot} style={{
                background: 'rgba(196,151,90,0.12)', border: 'none', borderRadius: 6,
                padding: '5px 12px', color: '#C4975A', fontSize: 12, fontWeight: 600,
                cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                <Icons.Plus /> Add Marriage
              </button>
            </div>

            {marriages.map((m, idx) => (
              <div key={idx} style={{
                background: '#FAF7F2', borderRadius: 12, border: '1px solid #EBE4D8',
                padding: 16, marginBottom: 10,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#C4975A', textTransform: 'uppercase' }}>
                    {marriages.length > 1 ? `Marriage ${idx + 1}` : 'Marriage'}
                  </span>
                  <button onClick={() => removeMarriage(idx)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', color: '#C0392B', fontSize: 11, fontWeight: 600,
                  }}>Remove</button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {/* Spouse */}
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Spouse</label>
                    <select value={m.spouseId} onChange={e => updateMarriage(idx, 'spouseId', e.target.value)} style={selectStyle}>
                      <option value="">— Select existing person —</option>
                      {peopleList.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>

                  <div>
                    <label style={labelStyle}>Marriage Date</label>
                    <input value={m.marriageDate} onChange={e => updateMarriage(idx, 'marriageDate', e.target.value)} placeholder="YYYY-MM-DD" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Marriage Place</label>
                    <input value={m.marriagePlace} onChange={e => updateMarriage(idx, 'marriagePlace', e.target.value)} placeholder="City, State" style={inputStyle} />
                  </div>

                  {/* Status toggle */}
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Status</label>
                    <div style={{ display: 'flex', gap: 10 }}>
                      {['current', 'ended'].map(s => (
                        <button key={s} onClick={() => updateMarriage(idx, 'status', s)} style={{
                          flex: 1, padding: '9px 0', borderRadius: 8,
                          border: m.status === s ? '2px solid #C4975A' : '1.5px solid #D5CAB8',
                          background: m.status === s ? '#F5EDE3' : '#fff',
                          color: m.status === s ? '#C4975A' : '#6B5E4F',
                          fontSize: 13, fontWeight: 600, cursor: 'pointer',
                          fontFamily: "'DM Sans', sans-serif",
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        }}>
                          {s === 'current' ? <><Icons.Heart /> Current</> : <><Icons.Unlink /> Ended</>}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* End reason (conditional) */}
                  {m.status === 'ended' && (
                    <>
                      <div style={{ gridColumn: 'span 2' }}>
                        <label style={labelStyle}>How did this marriage end?</label>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {[
                            { value: 'divorce', label: 'Divorce', color: '#BF6C00', bg: '#FFF3E0' },
                            { value: 'death', label: 'Death of Spouse', color: '#8B7D6B', bg: '#F0EBE3' },
                            { value: 'annulment', label: 'Annulment', color: '#C0392B', bg: '#FDE8E8' },
                            { value: 'separation', label: 'Legal Separation', color: '#9A7D0A', bg: '#FFF8E1' },
                          ].map(r => (
                            <button key={r.value} onClick={() => updateMarriage(idx, 'endReason', r.value)} style={{
                              padding: '7px 14px', borderRadius: 8,
                              border: m.endReason === r.value ? `2px solid ${r.color}` : '1.5px solid #D5CAB8',
                              background: m.endReason === r.value ? r.bg : '#fff',
                              color: m.endReason === r.value ? r.color : '#6B5E4F',
                              fontSize: 12, fontWeight: 600, cursor: 'pointer',
                              fontFamily: "'DM Sans', sans-serif",
                            }}>{r.label}</button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label style={labelStyle}>Date Ended</label>
                        <input value={m.endDate} onChange={e => updateMarriage(idx, 'endDate', e.target.value)} placeholder="YYYY-MM-DD" style={inputStyle} />
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}

            {marriages.length === 0 && (
              <p style={{ fontSize: 12, color: '#A0937F', textAlign: 'center', padding: '8px 0' }}>
                No marriages added — click "+ Add Marriage" if applicable
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid #E8E0D4',
          display: 'flex', justifyContent: 'flex-end', gap: 10,
        }}>
          <button onClick={onClose} style={{
            padding: '10px 24px', borderRadius: 8, border: '1.5px solid #D5CAB8',
            background: '#fff', color: '#6B5E4F', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
          }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving || !firstName.trim()} style={{
            padding: '10px 28px', borderRadius: 8, border: 'none',
            background: saving || !firstName.trim() ? '#B8AFA0' : '#3B2F1E',
            color: '#F5EDE3', fontSize: 13, fontWeight: 600,
            cursor: saving ? 'wait' : 'pointer', fontFamily: "'DM Sans', sans-serif",
          }}>{saving ? 'Saving...' : 'Add Person'}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, span = 1 }) {
  return (
    <div style={{ gridColumn: span === 2 ? 'span 2' : 'span 1' }}>
      <label style={labelStyle}>{label}</label>
      <input
        type="text" value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} style={inputStyle}
      />
    </div>
  );
}
